import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Pill, Spinner, type PillTone } from '@/shared/ui';
import { formatShortDate } from '@/shared/utils/format';
import { useVideoExports } from '@/features/exports/hooks/useVideoExports';
import { describeExportFailure } from '@/features/exports/services/exportsApi';
import {
  isActiveExport,
  type ExportPreset,
  type ExportPresetList,
  type ExportRecord,
  type ExportStatus,
} from '@/features/exports/types';

const STATUS_LABEL: Record<ExportStatus, { text: string; tone: PillTone }> = {
  queued: { text: 'Waiting', tone: 'neutral' },
  processing: { text: 'Converting…', tone: 'info' },
  completed: { text: 'Ready', tone: 'green' },
  failed: { text: 'Failed', tone: 'danger' },
  cancelled: { text: 'Cancelled', tone: 'neutral' },
  expired: { text: 'Expired', tone: 'warn' },
  deleted: { text: 'Removed', tone: 'neutral' },
};

const EXPIRED_COPY = 'This export has expired. Create it again to download it.';

const size = (p: { canvas_width: number; canvas_height: number }) => `${p.canvas_width}×${p.canvas_height}`;
const megabytes = (bytes: number) => `${(bytes / 1_000_000).toFixed(1)} MB`;

/** `verified_on` is a calendar date, not an instant: format it in UTC so it never slips a day. */
function outputFacts(e: ExportRecord): string {
  const o = e.output;
  const parts: string[] = [];
  if (o?.width && o.height) parts.push(`${o.width}×${o.height}`);
  if (o?.fps) parts.push(`${Math.round(o.fps * 100) / 100} fps`);
  if (o?.size_bytes) parts.push(megabytes(o.size_bytes));
  const until = formatShortDate(e.expires_at);
  if (until) parts.push(`kept until ${until}`);
  return parts.join(' · ');
}

export type ExportDialogProps = {
  open: boolean;
  videoId: string;
  presets: ExportPresetList;
  onClose: () => void;
};

export function ExportDialog({ open, videoId, presets, onClose }: ExportDialogProps) {
  const x = useVideoExports(videoId, open);
  const groupId = useId();
  const firstAvailable = presets.presets.find((p) => p.available)?.key ?? null;
  const [selected, setSelected] = useState<string | null>(firstAvailable);

  /* Never leave a now-unavailable preset selected (presets are per video and can change). */
  useEffect(() => {
    setSelected((cur) =>
      cur && presets.presets.some((p) => p.key === cur && p.available) ? cur : firstAvailable,
    );
  }, [presets, firstAvailable]);

  const labelOf = (key: string) => presets.presets.find((p) => p.key === key)?.label ?? 'Export';
  const locked = Boolean(x.active) || x.coolingDown;
  const showCreateError = x.createError && !x.conflict;

  /* The button is in the footer but the answer to it lands below the format list, which on
     a phone is off screen: bring a refusal into view so tapping Create is never silent. */
  const refusalEl = useRef<HTMLElement | null>(null);
  const refusalRef = (el: HTMLElement | null) => {
    refusalEl.current = el;
  };
  const hasRefusal = Boolean(showCreateError || x.conflict);
  useEffect(() => {
    if (hasRefusal) refusalEl.current?.scrollIntoView?.({ block: 'nearest' });
  }, [hasRefusal, x.createError, x.conflict]);

  const listBlock = (
    <section className="export-list" aria-labelledby={`${groupId}-list`}>
      <h4 className="export-list__title" id={`${groupId}-list`}>
        Your exports of this video
      </h4>
      {x.loading && x.exports.length === 0 ? (
        <p className="export-body__hint">
          <Spinner size={14} /> Loading…
        </p>
      ) : null}
      {x.loadError ? (
        <p className="export-error">
          {x.loadError}{' '}
          <Button size="sm" variant="ghost" onClick={() => void x.reload()}>
            Try again
          </Button>
        </p>
      ) : null}
      {!x.loading && !x.loadError && x.exports.length === 0 ? (
        <p className="export-body__hint">Nothing exported yet.</p>
      ) : null}
      {x.exports.length > 0 ? (
        <ul className="export-list__items">
          {x.exports.map((e) => (
            <ExportItem
              key={e.id}
              record={e}
              label={labelOf(e.preset)}
              busy={x.busyItems[e.id]}
              error={x.itemErrors[e.id]}
              canCreateAgain={!locked && !x.creating}
              onCancel={() => void x.cancel(e.id)}
              onDownload={() => void x.download(e.id)}
              onCreateAgain={() => {
                setSelected(e.preset);
                void x.create(e.preset);
              }}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Export for a platform"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            loading={x.creating}
            disabled={!selected || locked}
            onClick={() => selected && void x.create(selected)}
          >
            Create export
          </Button>
        </>
      }
    >
      <div className="export-body">
        <div className="export-layout" role="note">
          <p className="export-layout__title">How your video fits the frame</p>
          <p className="export-layout__text">{presets.layout_note}</p>
        </div>
        <p className="export-body__note">
          Exports are free. We keep each one for {presets.retention_days}{' '}
          {presets.retention_days === 1 ? 'day' : 'days'}, then delete it.
        </p>

        {x.active ? listBlock : null}

        <fieldset className="export-presets">
          <legend className="export-presets__legend">Choose a format</legend>
          {presets.presets.map((p) => (
            <PresetOption
              key={p.key}
              preset={p}
              groupId={groupId}
              checked={selected === p.key}
              onSelect={() => setSelected(p.key)}
            />
          ))}
        </fieldset>

        {x.active ? (
          <p className="export-body__hint">
            An export is running for this video. You can make another once it finishes.
          </p>
        ) : null}

        {showCreateError ? (
          <p className="export-error" role="alert" ref={refusalRef}>
            {x.createError}
          </p>
        ) : null}

        {x.conflict ? (
          <div className="export-conflict" role="alert" ref={refusalRef}>
            <p className="export-conflict__text">
              You already have an export running.{' '}
              {x.conflict.videoId === videoId
                ? 'It is listed below.'
                : 'It is for a different video. Wait for it to finish, or cancel it.'}
            </p>
            <div className="export-actions">
              <Button
                size="sm"
                loading={x.busyItems[x.conflict.exportId] === 'cancel'}
                onClick={() => x.conflict && void x.cancel(x.conflict.exportId, x.conflict.videoId)}
              >
                Cancel that export
              </Button>
              {x.conflict.videoId !== videoId ? (
                <Link className="export-link" to={`/videos/${encodeURIComponent(x.conflict.videoId)}`} onClick={onClose}>
                  Open that video
                </Link>
              ) : null}
            </div>
            {x.itemErrors[x.conflict.exportId] ? (
              <p className="export-error">{x.itemErrors[x.conflict.exportId]}</p>
            ) : null}
          </div>
        ) : null}

        {x.active ? null : listBlock}

        {/* Always mounted, so it exists before its text changes. */}
        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {x.announcement}
        </p>
      </div>
    </Modal>
  );
}

function PresetOption({
  preset: p,
  groupId,
  checked,
  onSelect,
}: {
  preset: ExportPreset;
  groupId: string;
  checked: boolean;
  onSelect: () => void;
}) {
  const reasonId = `${groupId}-${p.key}-reason`;
  return (
    <div className={`export-preset${p.available ? '' : ' export-preset--off'}`}>
      <label className="export-preset__main">
        <input
          className="export-preset__radio"
          type="radio"
          name={`${groupId}-preset`}
          value={p.key}
          checked={checked}
          disabled={!p.available}
          aria-describedby={p.available ? undefined : reasonId}
          onChange={onSelect}
        />
        <span className="export-preset__text">
          <span className="export-preset__label">{p.label}</span>
          <span className="export-preset__meta">
            {p.platform} · {p.content_type}
          </span>
          <span className="export-preset__summary">{p.summary}</span>
        </span>
        <span className="export-preset__size">{size(p)}</span>
      </label>

      {!p.available ? (
        <p className="export-preset__reason" id={reasonId}>
          Not available for this video: {p.unavailable_reason || 'it does not fit this format.'}
        </p>
      ) : null}
      {p.warnings.length > 0 ? (
        <ul className="export-preset__warnings">
          {p.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {p.limitations.length > 0 ? (
        <ul className="export-preset__limits">
          {p.limitations.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ExportItem({
  record: e,
  label,
  busy,
  error,
  canCreateAgain,
  onCancel,
  onDownload,
  onCreateAgain,
}: {
  record: ExportRecord;
  label: string;
  busy?: 'cancel' | 'download';
  error?: string;
  canCreateAgain: boolean;
  onCancel: () => void;
  onDownload: () => void;
  onCreateAgain: () => void;
}) {
  const status = STATUS_LABEL[e.status] ?? STATUS_LABEL.failed;
  const problem =
    e.status === 'failed'
      ? describeExportFailure(e.error)
      : e.status === 'expired'
        ? e.error?.message?.trim() || EXPIRED_COPY
        : null;
  const facts = e.status === 'completed' ? outputFacts(e) : '';

  return (
    <li className="export-item" data-status={e.status}>
      <div className="export-item__head">
        <span className="export-item__title">{label}</span>
        <Pill tone={status.tone} dot={isActiveExport(e)} size="sm">
          {status.text}
        </Pill>
      </div>
      <p className="export-item__when">Started {formatShortDate(e.created_at)}</p>
      {facts ? <p className="export-item__facts">{facts}</p> : null}
      {problem ? <p className="export-item__problem">{problem}</p> : null}
      {error ? (
        <p className="export-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="export-actions">
        {isActiveExport(e) ? (
          <Button size="sm" loading={busy === 'cancel'} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        {e.status === 'completed' ? (
          <Button size="sm" variant="primary" loading={busy === 'download'} onClick={onDownload}>
            Download
          </Button>
        ) : null}
        {e.status === 'expired' || e.status === 'failed' ? (
          <Button size="sm" disabled={!canCreateAgain} onClick={onCreateAgain}>
            Create again
          </Button>
        ) : null}
      </div>
    </li>
  );
}
